import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { TicketIcon, ListIcon, XIcon, DownloadSimpleIcon } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { APP_URL } from '../lib/appUrl';

const APP_STORE_URL = 'https://apps.apple.com/us/app/portal-event-tickets/id6766731094';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.bankrollcollective.portalapp';
const storeUrl = typeof navigator !== 'undefined' && /android/i.test(navigator.userAgent) ? PLAY_STORE_URL : APP_STORE_URL;

export default function Navbar() {
    const { pathname } = useLocation();
    const onAbout = pathname.startsWith('/about');
    const onContact = pathname.startsWith('/contact');
    const onEvents = pathname === '/';
    const [open, setOpen] = useState(false);
    const menuRef = useRef(null);

    // Close on route change
    useEffect(() => { setOpen(false); }, [pathname]);

    // Click outside / Esc to close
    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false); };
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDoc);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDoc);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    const navItem = (to, label, active, testid) => (
        <Link
            to={to}
            data-testid={testid}
            className={`block px-5 py-4 text-sm uppercase tracking-[0.2em] font-mono border-b border-white/5 last:border-b-0 transition-colors ${
                active ? 'text-neon bg-white/[0.03]' : 'text-white/80 hover:text-white hover:bg-white/[0.03]'
            }`}
        >
            <span className="flex items-center justify-between">
                {label}
                {active && <span className="w-1.5 h-1.5 rounded-full bg-neon" />}
            </span>
        </Link>
    );

    return (
        <header
            data-testid="app-navbar"
            className="sticky top-0 z-40 backdrop-blur-xl bg-black/60 border-b border-white/10"
        >
            <div className="max-w-7xl mx-auto flex items-center justify-between px-6 lg:px-12 h-16">
                <Link to="/" className="flex items-center gap-3 group" data-testid="brand-link">
                    <div className="w-9 h-9 bg-neon flex items-center justify-center group-hover:rotate-6 transition-transform">
                        <span className="font-display text-2xl text-black leading-none translate-y-[1px]">P</span>
                    </div>
                    <span className="hidden sm:inline font-display text-2xl tracking-tight uppercase">Portal</span>
                </Link>

                <div className="flex items-center gap-2 sm:gap-3">
                    <a href={storeUrl} className="md:hidden" data-testid="download-app-btn">
                        <Button
                            className="bg-transparent border border-neon text-neon hover:bg-neon hover:text-black rounded-none uppercase tracking-[0.18em] text-xs font-bold px-4"
                        >
                            <DownloadSimpleIcon size={14} weight="bold" className="mr-2" />
                            Get app
                        </Button>
                    </a>
                    <a href={APP_URL} className="hidden sm:block">
                        <Button
                            className="bg-neon text-black hover:bg-[#E8FF6B] rounded-none uppercase tracking-[0.18em] text-xs font-bold px-4 sm:px-6"
                            data-testid="getin-btn"
                        >
                            <TicketIcon size={14} weight="bold" className="mr-2" />
                            Get in
                        </Button>
                    </a>

                    <div className="relative" ref={menuRef}>
                        <button
                            type="button"
                            onClick={() => setOpen((v) => !v)}
                            aria-label={open ? 'Close menu' : 'Open menu'}
                            aria-expanded={open}
                            aria-controls="primary-menu"
                            data-testid="menu-toggle"
                            className="w-10 h-10 flex items-center justify-center border border-white/15 hover:border-neon hover:text-neon text-white/80 transition-colors"
                        >
                            {open ? <XIcon size={18} weight="bold" /> : <ListIcon size={20} weight="bold" />}
                        </button>

                        {open && (
                            <div
                                id="primary-menu"
                                role="menu"
                                data-testid="menu-dropdown"
                                className="absolute right-0 top-12 min-w-[220px] bg-[#0A0A0B] border border-white/15 shadow-[0_20px_60px_-10px_rgba(0,0,0,0.8)] backdrop-blur-xl"
                            >
                                {navItem('/', 'Events', onEvents, 'menu-events')}
                                {navItem('/about', 'About', onAbout, 'menu-about')}
                                {navItem('/contact', 'Contact', onContact, 'menu-contact')}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </header>
    );
}
